import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { DashboardHeader } from "@/components/dashboard-header";
import { EnhancedDocumentGrid } from "@/components/enhanced-document-grid";
import { ActivityFeed } from "@/components/activity-feed";
import { UploadSection } from "@/components/upload-section";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Activity, X } from "lucide-react";
import type { DocumentWithCounts } from "@shared/schema";

export default function Home() {
  const [, setLocation] = useLocation();
  const [showUpload, setShowUpload] = useState(false);
  const { toast } = useToast();
  const { user: currentUser, isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  if (isLoading || !isAuthenticated || !currentUser) {
    return null;
  }

  const { data: documents = [], isLoading: documentsLoading } = useQuery<DocumentWithCounts[]>({
    queryKey: ["/api/documents"],
  });

  const handleDocumentSelect = (document: DocumentWithCounts) => {
    setLocation(`/doc/${document.id}`);
  };

  const handleCreateDocument = () => {
    setShowUpload(true);
  };

  const handleUploadComplete = () => {
    setShowUpload(false);
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-background accent-mesh">
      <DashboardHeader
        organization={{ 
          id: currentUser.organizationId, 
          name: "Organization", 
          slug: "org", 
          createdAt: new Date() 
        }}
        currentUser={currentUser}
      />

      <main className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem
            value="activity"
            className="border border-border rounded-md bg-card px-4 shadow-sm"
          >
            <AccordionTrigger className="hover:no-underline py-3 text-sm font-medium text-foreground gap-2 [&>svg]:ml-auto [&>svg]:shrink-0">
              <span className="flex items-center gap-2">
                <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Activity
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pt-1 pb-3">
              <ActivityFeed organizationId={currentUser.organizationId} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <EnhancedDocumentGrid
          documents={documents}
          onSelectDocument={handleDocumentSelect}
          onCreateDocument={handleCreateDocument}
          isLoading={documentsLoading}
          organizationId={currentUser.organizationId}
        />
      </main>

      {showUpload && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-medium text-foreground">
                  Upload Document
                </h2>
                <button
                  onClick={() => setShowUpload(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors h-7 w-7 flex items-center justify-center rounded hover:bg-accent"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <UploadSection 
                documentColor="hsl(258, 90%, 60%)"
                onUploadComplete={handleUploadComplete}
                onNavigateToVersion={(documentId, versionNumber) => {
                  setShowUpload(false);
                  setLocation(`/doc/${documentId}/comment/v/${versionNumber}/p/1`);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
